import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Database,
  ExternalLink,
  FileSearch,
  History,
  Home,
  Loader2,
  Mail,
  MapPin,
  Network,
  Phone,
  RefreshCw,
  Search,
  Save,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  XCircle,
} from 'lucide-react';
import { communicationsRepository } from './api/communications';
import type { LookupCandidate, LookupProviderHealth, LookupSaveResult, LookupSearch, LookupUsage } from './domain';

const DISCLAIMER = 'Use lookup data for legitimate safety, identity verification, fraud prevention, and personal research. Verify important information using authoritative sources.';
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

export function LookupPage({ businessId, lookupId, onLookupOpen, onLookupBack, onClose }: {
  businessId: string;
  lookupId: string | null;
  onLookupOpen: (id: string) => void;
  onLookupBack: () => void;
  onClose: () => void;
}) {
  const [searchMode, setSearchMode] = useState<'person' | 'address'>('person');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [address, setAddress] = useState('');
  const [strategy, setStrategy] = useState<'fast' | 'standard'>('standard');
  const [active, setActive] = useState<LookupSearch | null>(null);
  const [history, setHistory] = useState<LookupSearch[]>([]);
  const [providers, setProviders] = useState<LookupProviderHealth[]>([]);
  const [usage, setUsage] = useState<LookupUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [operation, setOperation] = useState<'save' | 'refresh' | 'paid' | null>(null);
  const [saveResult, setSaveResult] = useState<LookupSaveResult | null>(null);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    try {
      const [recent, sourceHealth, currentUsage] = await Promise.all([
        communicationsRepository.getLookupHistory(20),
        communicationsRepository.getLookupProviders(),
        communicationsRepository.getLookupUsage(),
      ]);
      setHistory(recent);
      setProviders(sourceHealth);
      setUsage(currentUsage);
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Lookup is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadOverview(); }, [loadOverview]);

  useEffect(() => {
    if (!lookupId) {
      setActive(null);
      setSaveResult(null);
      return;
    }
    if (active?.id === lookupId) return;
    let cancelled = false;
    setSaveResult(null);
    communicationsRepository.getLookup(lookupId)
      .then(result => { if (!cancelled) setActive(result); })
      .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'That lookup could not be opened.'); });
    return () => { cancelled = true; };
  }, [lookupId, active?.id]);

  useEffect(() => {
    if (!active || !['queued', 'running', 'partial'].includes(active.status)) return;
    const timer = window.setInterval(async () => {
      try {
        const current = await communicationsRepository.getLookup(active.id);
        setActive(current);
        if (current.status === 'complete' || current.status === 'failed') void loadOverview();
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : 'Lookup status could not be refreshed.');
      }
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [active?.id, active?.status, loadOverview]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const personReady = Boolean(name.trim() || phone.trim());
    const addressReady = Boolean(address.trim());
    if ((searchMode === 'person' ? !personReady : !addressReady) || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await communicationsRepository.createLookup(searchMode === 'address' ? {
        type: 'address', address: address.trim(), strategy,
        businessId: businessId === 'all' ? undefined : businessId,
      } : name.trim() ? {
        type: 'person', name: name.trim(), phone: phone.trim() || undefined,
        city: city.trim() || undefined, state: state || undefined, strategy,
        businessId: businessId === 'all' ? undefined : businessId,
      } : {
        type: 'phone', phone: phone.trim(), strategy,
        businessId: businessId === 'all' ? undefined : businessId,
      });
      setActive(result);
      setSaveResult(null);
      onLookupOpen(result.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The lookup could not be started.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearSearch = () => {
    setName(''); setPhone(''); setCity(''); setState(''); setAddress(''); setError('');
  };

  const searchReady = searchMode === 'person' ? Boolean(name.trim() || phone.trim()) : Boolean(address.trim());

  const openHistory = (item: LookupSearch) => {
    setError('');
    setSaveResult(null);
    onLookupOpen(item.id);
  };

  const saveActive = async () => {
    if (!active || operation) return;
    setOperation('save');
    setError('');
    try {
      const result = await communicationsRepository.saveLookup(active.id);
      setSaveResult(result);
      setActive(current => current?.id === active.id ? { ...current, saved: true } : current);
      void loadOverview();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The profile could not be saved.');
    } finally {
      setOperation(null);
    }
  };

  const refreshActive = async () => {
    if (!active || operation) return;
    setOperation('refresh');
    setError('');
    try {
      const refreshed = await communicationsRepository.refreshLookup(active.id);
      setActive(refreshed);
      setSaveResult(null);
      onLookupOpen(refreshed.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The profile could not be refreshed.');
    } finally {
      setOperation(null);
    }
  };

  const enrichActive = async () => {
    if (!active || operation) return;
    setOperation('paid');
    setError('');
    try {
      const enriched = await communicationsRepository.runPaidLookupEnrichment(active.id);
      setActive(enriched);
      void loadOverview();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Paid enrichment could not be completed.');
    } finally {
      setOperation(null);
    }
  };

  return <main className="lookup-workspace">
    <header className="lookup-topbar">
      <button className="lookup-back" onClick={onClose}><ArrowLeft size={17} /> Communications</button>
      <span className="lookup-private"><ShieldCheck size={14} /> Private workspace</span>
    </header>

    <section className="lookup-hero">
      <div className="lookup-hero-copy">
        <p className="eyebrow">Identity & property intelligence</p>
        <h1>Lookup</h1>
        <p>Start with a phone, person, or address. LineWatch builds one sourced profile without interrupting calls or messages.</p>
      </div>
      <form className="lookup-search lookup-search-structured" onSubmit={submit}>
        <div className="lookup-search-heading">
          <span><Search size={20} /><span><strong>Search details</strong><small>Use every detail you know for a stronger match.</small></span></span>
          <div className="lookup-search-modes" role="tablist" aria-label="Lookup type">
            <button type="button" role="tab" aria-selected={searchMode === 'person'} className={searchMode === 'person' ? 'active' : ''} onClick={() => setSearchMode('person')}><UserRound size={14} /> Person or phone</button>
            <button type="button" role="tab" aria-selected={searchMode === 'address'} className={searchMode === 'address' ? 'active' : ''} onClick={() => setSearchMode('address')}><Home size={14} /> Address</button>
          </div>
        </div>
        {searchMode === 'person' ? <div className="lookup-field-grid">
          <label className="lookup-field lookup-field-name"><span><UserRound size={14} /> Full name</span><input value={name} onChange={event => setName(event.target.value)} placeholder="Kim Barker" autoComplete="name" /></label>
          <label className="lookup-field lookup-field-phone"><span><Phone size={14} /> Phone number</span><input value={phone} onChange={event => setPhone(event.target.value)} placeholder="(435) 555-1234" inputMode="tel" autoComplete="tel" /></label>
          <label className="lookup-field lookup-field-city"><span><MapPin size={14} /> City</span><input value={city} onChange={event => setCity(event.target.value)} placeholder="Ogden" autoComplete="address-level2" /></label>
          <label className="lookup-field lookup-field-state"><span><MapPin size={14} /> State</span><select value={state} onChange={event => setState(event.target.value)} aria-label="State" autoComplete="address-level1"><option value="">Any</option>{US_STATES.map(code => <option key={code} value={code}>{code}</option>)}</select></label>
        </div> : <div className="lookup-address-field">
          <label className="lookup-field"><span><Home size={14} /> Street address</span><input value={address} onChange={event => setAddress(event.target.value)} placeholder="123 Main Street, Ogden, UT 84401" autoComplete="street-address" /></label>
        </div>}
        <div className="lookup-search-footer">
          <small>{searchMode === 'person' ? 'Name + location narrows candidates. A phone number gives the strongest identity link.' : 'Address search validates the location and checks available resident and parcel sources.'}</small>
          {(name || phone || city || state || address) && <button type="button" className="lookup-clear" onClick={clearSearch}><XCircle size={15} /> Clear</button>}
          <button className="lookup-submit" type="submit" disabled={!searchReady || submitting}>{submitting ? <Loader2 className="spinning" size={17} /> : <Search size={17} />}<span>Search</span></button>
        </div>
      </form>
      <div className="lookup-options">
        <span>Search depth</span>
        <button className={strategy === 'fast' ? 'active' : ''} onClick={() => setStrategy('fast')}>Fast</button>
        <button className={strategy === 'standard' ? 'active' : ''} onClick={() => setStrategy('standard')}>Standard</button>
        <small>CRM-unknown callers run deep free sources, then every applicable enabled enrichment source within budget. Deep OSINT stays explicit.</small>
      </div>
      {error && <div className="lookup-error"><AlertTriangle size={16} />{error}</div>}
    </section>

    {active
      ? <LookupResult search={active} providers={providers} operation={operation} saveResult={saveResult} onSave={saveActive} onRefresh={refreshActive} onPaid={enrichActive} onClose={onLookupBack} />
      : <LookupOverview history={history} providers={providers} usage={usage} loading={loading} onOpen={openHistory} />}

    <footer className="lookup-disclaimer"><ShieldCheck size={14} /><span>{DISCLAIMER}</span></footer>
  </main>;
}

function LookupOverview({ history, providers, usage, loading, onOpen }: {
  history: LookupSearch[];
  providers: LookupProviderHealth[];
  usage: LookupUsage | null;
  loading: boolean;
  onOpen: (item: LookupSearch) => void;
}) {
  return <div className="lookup-overview-grid">
    <section className="lookup-panel lookup-history-panel">
      <div className="lookup-panel-heading"><span><History size={17} /><strong>Recent lookups</strong></span><small>5 visible · newest first</small></div>
      {loading && <LookupLoading label="Loading lookup history" />}
      {!loading && !history.length && <div className="lookup-empty"><FileSearch size={28} /><strong>Your lookup history starts here</strong><p>Search a caller, contact, person, or property above.</p></div>}
      {!loading && history.length > 0 && <div className="lookup-history-list" role="region" aria-label="Recent lookup history, newest first" tabIndex={history.length > 5 ? 0 : -1}>{history.map(item => <button className="lookup-history-row" key={item.id} onClick={() => onOpen(item)}>
        <span className={`lookup-type-icon ${item.input.type}`}>{item.input.type === 'phone' ? <Phone size={17} /> : item.input.type === 'address' ? <Home size={17} /> : <UserRound size={17} />}</span>
        <span><strong>{item.candidates[0]?.displayName || item.input.displayQuery}</strong><small>{item.input.type} · {formatWhen(item.createdAt)}</small></span>
        <StatusPill status={item.status} />
        <ChevronRight size={16} />
      </button>)}</div>}
    </section>

    <aside className="lookup-side-stack">
      <section className="lookup-panel">
        <div className="lookup-panel-heading"><span><Database size={17} /><strong>Source readiness</strong></span></div>
        <div className="lookup-provider-list">
          {!providers.length && !loading && <p className="lookup-muted">Provider adapters are being connected.</p>}
          {providers.map(provider => <div className="lookup-provider-row" key={provider.provider}>
            <span className={`provider-dot ${provider.status}`} />
            <span><strong>{providerLabel(provider.provider)}</strong><small>{provider.capabilities.join(' · ') || 'No capability'}</small></span>
            <em>{provider.status}</em>
          </div>)}
        </div>
      </section>
      <section className="lookup-panel lookup-usage">
        <div className="lookup-panel-heading"><span><Sparkles size={17} /><strong>Lookup usage</strong></span></div>
        <div>
          <span><strong>{usage?.today.searches || 0}</strong><small>Today</small></span>
          <span><strong>{usage?.month.searches || 0}</strong><small>This month</small></span>
          <span><strong>{usage?.apifyMonthlyBudget !== undefined ? `${currency(usage.apifyMonthlySpend || 0)} / ${currency(usage.apifyMonthlyBudget)}` : '—'}</strong><small>Apify</small></span>
          <span><strong>{usage?.monthlyBudget !== undefined ? `${currency(usage.paidMonthlySpend || 0)} / ${currency(usage.monthlyBudget)}` : currency(usage?.month.estimatedSpend || 0)}</strong><small>{usage?.monthlyBudget !== undefined ? 'Trestle' : 'Est. spend'}</small></span>
        </div>
      </section>
    </aside>
  </div>;
}

function LookupResult({ search, providers, operation, saveResult, onSave, onRefresh, onPaid, onClose }: {
  search: LookupSearch;
  providers: LookupProviderHealth[];
  operation: 'save' | 'refresh' | 'paid' | null;
  saveResult: LookupSaveResult | null;
  onSave: () => void;
  onRefresh: () => void;
  onPaid: () => void;
  onClose: () => void;
}) {
  const primary = search.candidates[0];
  const running = ['queued', 'running', 'partial'].includes(search.status);
  const enrichmentProviders = providers.filter(provider => provider.provider === 'apify' || provider.provider === 'trestle' || provider.provider.startsWith('trestle-'));
  const enrichmentRuns = search.providerRuns.filter(run => run.provider === 'apify' || run.provider === 'trestle' || run.provider.startsWith('trestle-'));
  const enrichmentReady = enrichmentProviders.some(provider => provider.enabled && provider.status === 'ready');
  const enrichmentComplete = enrichmentRuns.length > 0 && enrichmentRuns.every(run => !['queued', 'running'].includes(run.status));
  const enrichmentMaximumCost = enrichmentProviders.reduce((total, provider) => total + (provider.estimatedCost || 0), 0);
  return <section className="lookup-result">
    <div className="lookup-result-toolbar">
      <button onClick={onClose}><History size={15} /> Recent lookups</button>
      <div className="lookup-result-actions">
        <span><Clock3 size={14} /> Updated {formatWhen(search.updatedAt)}</span>
        <button onClick={onRefresh} disabled={Boolean(operation) || running}>{operation === 'refresh' ? <Loader2 className="spinning" size={14} /> : <RefreshCw size={14} />} Refresh</button>
        <button className="lookup-save" onClick={onSave} disabled={Boolean(operation) || running || !primary || search.saved}>{operation === 'save' ? <Loader2 className="spinning" size={14} /> : search.saved ? <Check size={14} /> : <Save size={14} />}{search.saved ? 'Saved' : 'Save profile'}</button>
      </div>
    </div>
    {saveResult && <LookupSaveStatus result={saveResult} />}
    <div className="lookup-identity-card">
      <span className="lookup-identity-avatar">{primary?.displayName ? initials(primary.displayName) : search.input.type === 'phone' ? <Phone size={25} /> : search.input.type === 'address' ? <Home size={25} /> : <UserRound size={25} />}</span>
      <div className="lookup-identity-copy">
        <p>{search.input.type === 'phone' ? 'Phone lookup' : search.input.type === 'address' ? 'Address lookup' : 'Person lookup'}</p>
        <h2>{primary?.displayName || search.input.displayQuery}</h2>
        {primary ? <span className={`confidence confidence-${confidenceTone(primary.confidence)}`}><ShieldCheck size={14} />{primary.confidenceLabel} · {Math.round(primary.confidence)}%</span> : <span className="lookup-muted">No corroborated identity yet</span>}
        {primary && (primary.ageRange || primary.approximateAge || primary.gender) && <span className="lookup-demographics"><UserRound size={13} />{primary.ageRange ? `Age ${primary.ageRange}` : primary.approximateAge ? `Approx. age ${primary.approximateAge}` : ''}{(primary.ageRange || primary.approximateAge) && primary.gender ? ' · ' : ''}{primary.gender || ''}</span>}
      </div>
      <StatusPill status={search.status} />
    </div>

    {running && <div className="lookup-progress"><Loader2 className="spinning" size={18} /><div><strong>Building the profile</strong><span>Results appear as each independent source completes.</span></div></div>}
    {search.status === 'failed' && <div className="lookup-error"><AlertTriangle size={16} />{search.error || 'This lookup could not be completed.'}</div>}
    {!running && !primary && search.status === 'complete' && <div className="lookup-no-match"><Search size={24} /><div><strong>No reliable match yet</strong><p>The search completed without enough corroborated data to name a person. Provider details remain visible below.</p></div></div>}

    {search.candidates.length > 1 && <section className="lookup-candidates"><div className="lookup-section-title"><Users size={17} /><strong>Possible matches</strong><small>Kept separate until identity is certain</small></div>{search.candidates.map(candidate => <CandidateChoice key={candidate.id} candidate={candidate} />)}</section>}
    {primary && <ProfileSections candidate={primary} />}
    <ProviderRuns search={search} />
    <div className="lookup-enrichment-actions" aria-label="Optional enrichment">
      <button
        className={enrichmentComplete ? 'complete' : ''}
        disabled={Boolean(operation) || running || !enrichmentReady || enrichmentComplete}
        onClick={onPaid}
        title={!enrichmentReady ? 'No enrichment provider is configured for this lookup.' : undefined}
      >
        {operation === 'paid' ? <Loader2 className="spinning" size={15} /> : enrichmentComplete ? <Check size={15} /> : <Sparkles size={15} />}
        {operation === 'paid' ? 'Enriching…' : enrichmentComplete ? 'Enrichment complete' : `Run enrichment${enrichmentMaximumCost ? ` · up to ~${currency(enrichmentMaximumCost)}` : ''}`}
      </button>
      <button disabled title="Deep OSINT is disabled until SpiderFoot is isolated and configured."><Network size={15} /> Run Deep OSINT</button>
      <small>Order: free sources → Apify → Trestle. Each paid tier has its own monthly ceiling, and cached results do not add another provider charge.</small>
    </div>
  </section>;
}

function LookupSaveStatus({ result }: { result: LookupSaveResult }) {
  const crm = result.propagation.businessCrm;
  const linewatchUpdated = result.propagation.linewatch.status === 'updated';
  const tone = linewatchUpdated && ['synced', 'queued', 'not_configured'].includes(crm.status) ? 'success' : 'warning';
  const crmLabel = crm.status === 'synced'
    ? `${crm.businessName || 'Business'} CRM synced`
    : crm.status === 'queued'
      ? `${crm.businessName || 'Business'} CRM queued`
      : crm.status === 'not_configured'
        ? 'Business CRM not configured for this line'
        : crm.status === 'failed'
          ? 'Business CRM will retry automatically'
          : 'Business CRM was not linked';
  return <div className={`lookup-save-status ${tone}`} role="status" aria-live="polite">
    {tone === 'success' ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}
    <div>
      <strong>{linewatchUpdated ? `${result.propagation.linewatch.displayName || 'Contact'} saved to LineWatch` : 'Profile saved'}</strong>
      <span>{crmLabel}</span>
    </div>
  </div>;
}

function ProfileSections({ candidate }: { candidate: LookupCandidate }) {
  return <div className="lookup-profile-grid">
    <ProfileSection icon={Phone} title="Phone intelligence" count={candidate.phones.length}>
      {candidate.phones.map(phone => <article className="lookup-data-row" key={phone.numberE164}><div><strong>{phone.formattedNumber || phone.numberE164}</strong><span>{[phone.lineType, phone.carrier].filter(Boolean).join(' · ') || 'Telecom details pending'}</span></div>{phone.cnam && <em>{phone.cnam}</em>}</article>)}
    </ProfileSection>
    {candidate.linewatch && <ProfileSection icon={Network} title="LineWatch history" count={(candidate.linewatch.callCount || 0) + (candidate.linewatch.messageCount || 0)}>
      <div className="lookup-stat-grid"><span><strong>{candidate.linewatch.callCount || 0}</strong><small>Calls</small></span><span><strong>{candidate.linewatch.messageCount || 0}</strong><small>Messages</small></span><span><strong>{candidate.linewatch.businessName || 'All lines'}</strong><small>Business line</small></span></div>
    </ProfileSection>}
    <ProfileSection icon={MapPin} title="Addresses" count={candidate.addresses.length}>
      {candidate.addresses.map(address => <article className="lookup-data-row" key={address.formatted}><div><strong>{address.formatted}</strong><span>{address.kind === 'likely_current' ? 'Likely current address' : address.kind === 'possible_prior' ? 'Possible prior address' : 'Possible address'}</span></div>{address.confidence !== undefined && <em>{Math.round(address.confidence)}%</em>}</article>)}
    </ProfileSection>
    <ProfileSection icon={Mail} title="Emails" count={candidate.emails.length}>
      {candidate.emails.map(email => <article className="lookup-data-row" key={email.email}><div><strong>{email.email}</strong><span>{email.sources?.length || 0} sources</span></div></article>)}
    </ProfileSection>
    <ProfileSection icon={Building2} title="Properties" count={candidate.properties.length} wide>
      {candidate.properties.map(property => <article className="lookup-property" key={`${property.county}-${property.parcelNumber}-${property.address}`}><div><strong>{property.address}</strong><span>{property.county ? `${property.county} County` : 'Official property source'}</span></div><dl><div><dt>Owner</dt><dd>{property.ownerName || 'Not listed'}</dd></div><div><dt>Parcel</dt><dd>{property.parcelNumber || '—'}</dd></div><div><dt>Assessed</dt><dd>{property.assessedValue ? currency(property.assessedValue) : '—'}</dd></div></dl>{property.officialSourceUrl && <a href={property.officialSourceUrl} target="_blank" rel="noreferrer">Official record <ExternalLink size={12} /></a>}</article>)}
    </ProfileSection>
    <ProfileSection icon={Users} title="Possible relatives / associates" count={candidate.relationships.length}>
      {candidate.relationships.map((relationship, index) => <article className="lookup-data-row" key={`${relationship.name}-${index}`}><div><strong>{relationship.name}</strong><span>{relationship.label}{relationship.reason ? ` · ${relationship.reason}` : ''}</span></div></article>)}
    </ProfileSection>
    <ProfileSection icon={Database} title="Sources" count={candidate.sources.length}>
      {candidate.sources.map((source, index) => <article className="lookup-data-row" key={`${source.provider}-${source.source}-${index}`}><div><strong>{source.source}</strong><span>{providerLabel(source.provider)} · {formatWhen(source.retrievedAt)}</span></div>{source.corroborated && <CheckCircle2 size={15} />}</article>)}
    </ProfileSection>
  </div>;
}

function ProfileSection({ icon: Icon, title, count, wide = false, children }: { icon: typeof Phone; title: string; count: number; wide?: boolean; children: React.ReactNode }) {
  return <section className={`lookup-panel lookup-profile-section ${wide ? 'wide' : ''}`}>
    <div className="lookup-section-title"><Icon size={17} /><strong>{title}</strong><small>{count}</small></div>
    {count ? children : <p className="lookup-muted">No corroborated {title.toLowerCase()} returned.</p>}
  </section>;
}

function ProviderRuns({ search }: { search: LookupSearch }) {
  return <section className="lookup-panel lookup-provider-runs"><div className="lookup-section-title"><Database size={17} /><strong>Provider status</strong><small>{search.providerRuns.length}</small></div>
    {!search.providerRuns.length && <p className="lookup-muted">Provider adapters are being prepared for this search type.</p>}
    {search.providerRuns.map(run => <div className="lookup-run" key={run.id}>
      <span className={`run-state ${run.status}`}>{['complete', 'cached'].includes(run.status) ? <Check size={13} /> : run.status === 'running' ? <Loader2 className="spinning" size={13} /> : run.status === 'failed' ? <XCircle size={13} /> : <Clock3 size={13} />}</span>
      <span><strong>{providerLabel(run.provider)}</strong><small>{run.cacheHit ? 'Cached result' : run.durationMs !== undefined ? `${run.durationMs} ms` : run.status}</small></span>
      <em>{run.status}</em>
      {run.errorMessage && <p>{run.errorMessage}</p>}
    </div>)}
  </section>;
}

function CandidateChoice({ candidate }: { candidate: LookupCandidate }) {
  return <article className="lookup-candidate"><span>{candidate.rank}</span><div><strong>{candidate.displayName || 'Unnamed candidate'}</strong><small>{candidate.addresses[0]?.formatted || candidate.phones[0]?.formattedNumber || 'Identity details pending'}</small></div><em>{Math.round(candidate.confidence)}%</em></article>;
}

function StatusPill({ status }: { status: LookupSearch['status'] }) {
  return <span className={`lookup-status ${status}`}>{['queued', 'running', 'partial'].includes(status) && <Loader2 className="spinning" size={12} />}{status}</span>;
}

function LookupLoading({ label }: { label: string }) {
  return <div className="lookup-empty"><Loader2 className="spinning" size={24} /><strong>{label}</strong></div>;
}

function formatWhen(value: string): string {
  const date = new Date(value);
  const difference = Date.now() - date.getTime();
  if (difference < 60_000) return 'just now';
  if (difference < 3_600_000) return `${Math.max(1, Math.floor(difference / 60_000))}m ago`;
  if (difference < 86_400_000) return `${Math.floor(difference / 3_600_000)}h ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function providerLabel(value: string): string {
  return value.split(/[-_]/).map(part => part ? part[0]!.toUpperCase() + part.slice(1) : '').join(' ');
}

function initials(value: string): string {
  return value.split(/\s+/).slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
}

function confidenceTone(value: number): string {
  return value >= 80 ? 'high' : value >= 60 ? 'possible' : 'weak';
}

function currency(value: number): string {
  const cents = Math.abs(value) < 1;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
  }).format(value);
}
